import { formatLagosDate } from "./tripLog";

const MAX_PAGE_REQUESTS = 100;
const FALLBACK_LABEL = "N/A";

const isPresent = (value) => {
  if (value === null || value === undefined) return false;
  return typeof value === "string" ? value.trim().length > 0 : true;
};

const readPath = (source, path) => {
  if (!source || !path) return undefined;
  return path.split(".").reduce((value, key) => {
    if (value === null || value === undefined) return undefined;
    return value[key];
  }, source);
};

const asText = (value) => {
  if (!isPresent(value)) return null;
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim() || null;
  }
  return null;
};

export const asComparableId = (value) => {
  const primitive = asText(value);
  if (primitive) return primitive;
  if (!value || typeof value !== "object") return null;

  return (
    asText(value._id) ||
    asText(value.id) ||
    asText(value.riderId) ||
    asText(value.userId) ||
    asText(value.user?._id) ||
    asText(value.user?.id) ||
    asText(value.user?.riderId) ||
    null
  );
};

export const resolveRiderIdentity = (profile = {}, routeId = null) => {
  const profileId = asComparableId(profile.id || profile._id || routeId);
  const publicRiderId = asComparableId(
    profile.riderId || profile.user?.riderId || profile.user?.driverId,
  );
  const userId = asComparableId(
    profile.user?._id || profile.user?.id || profile.userId,
  );
  const allIds = [...new Set([profileId, publicRiderId, userId].filter(Boolean))];

  return { profileId, publicRiderId, userId, allIds };
};

const recordIdCandidates = (record = {}) => [
  record.riderId,
  record.driverId,
  record.rider,
  record.driver,
  record.assignedRider,
  record.assignedDriver,
  record.assignedRiderId,
  record.assignedDriverId,
  record.riderProfileId,
  record.riderUserId,
  record.driverProfileId,
  record.driverUserId,
  record.riderProfile,
  record.driverProfile,
  record.ride?.riderId,
  record.ride?.driverId,
  record.ride?.rider,
  record.ride?.driver,
  record.booking?.riderId,
  record.booking?.driverId,
  record.booking?.rider,
  record.booking?.driver,
];

export const extractRecordRiderIds = (record = {}) => [
  ...new Set(recordIdCandidates(record).map(asComparableId).filter(Boolean)),
];

export const recordBelongsToRider = (record, identity) => {
  if (!identity?.allIds?.length) return false;
  const knownIds = new Set(identity.allIds);
  return extractRecordRiderIds(record).some((id) => knownIds.has(id));
};

export const getCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const collectionKeys = ["data", ...keys, "items", "results"];
  for (const key of collectionKeys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  if (payload.data && typeof payload.data === "object") {
    return getCollection(payload.data, keys);
  }
  return [];
};

export const getPageMeta = (payload, fallbackPage = 1, fallbackLimit = 100) => {
  const pagination = payload?.pagination || {};
  const page = Number(pagination.page) || fallbackPage;
  const limit = Number(pagination.limit) || fallbackLimit;
  const total = Number(pagination.total) || 0;
  const totalPages = Number(pagination.totalPages) || 1;

  return {
    page,
    limit,
    total,
    totalPages,
    hasNext: Boolean(pagination.hasNext ?? page < totalPages),
    hasPrev: Boolean(pagination.hasPrev ?? page > 1),
  };
};

export const getRecordId = (record = {}) => {
  const candidates = [
    record._id,
    record.id,
    record.bookingId,
    record.rideId,
    record.rideId?._id,
    record.rideId?.id,
    record.booking?._id,
    record.booking?.id,
  ];

  for (const candidate of candidates) {
    const id = asComparableId(candidate);
    if (id) return id;
  }
  return null;
};

export const fetchAllPages = async (fetchPage, pageSize = 100) => {
  const records = [];
  const seenIds = new Set();
  let page = 1;
  let pageMeta = getPageMeta(null, page, pageSize);
  let requests = 0;

  for (;;) {
    // Fail rather than hand back a partial list that looks complete.
    if (requests >= MAX_PAGE_REQUESTS) {
      throw new Error(`Stopped after ${MAX_PAGE_REQUESTS} pages; the list is incomplete.`);
    }
    requests += 1;
    const payload = await fetchPage(page, pageSize);
    const pageRecords = getCollection(payload, ["rides", "bookings", "commissions", "earnings"]);

    pageRecords.forEach((record, index) => {
      const recordId = getRecordId(record);
      const dedupeKey = recordId || `${page}:${index}`;
      if (!seenIds.has(dedupeKey)) {
        seenIds.add(dedupeKey);
        records.push(record);
      }
    });

    pageMeta = getPageMeta(payload, page, pageSize);
    const reachedLastPage = page >= pageMeta.totalPages;
    const noNextPage = pageMeta.hasNext === false;
    const noRecords = pageRecords.length === 0;
    if (reachedLastPage || noNextPage || noRecords) break;

    page += 1;
  }

  return { records, pagination: pageMeta };
};

const getNumeric = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || value.trim() === "") return null;

  const numeric = Number(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
};

export const getMoneyInNaira = (record = {}, candidates = []) => {
  for (const candidate of candidates) {
    const path = typeof candidate === "string" ? candidate : candidate.path;
    const value = readPath(record, path);
    const numeric = getNumeric(value);
    if (numeric === null) continue;

    const unit = typeof candidate === "object" && candidate.unit
      ? candidate.unit
      : /kobo/i.test(path)
        ? "kobo"
        : "naira";
    return unit === "kobo" ? numeric / 100 : numeric;
  }
  return null;
};

export const normalizeStatus = (value) => {
  const status = asText(value)?.toLowerCase().replace(/[-\s]+/g, "_");
  if (!status) return "unknown";
  if (status === "canceled") return "cancelled";
  if (["ongoing", "in_progress", "inprogress"].includes(status)) return "active";
  return status;
};

export const formatStatusLabel = (value) => {
  const normalized = normalizeStatus(value);
  if (normalized === "unknown") return FALLBACK_LABEL;
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const formatCurrencyValue = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return FALLBACK_LABEL;
  }

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const getDateValue = (record = {}) =>
  [
    record.createdAt,
    record.requestedAt,
    record.rideTime,
    record.startedAt,
    record.completedAt,
    record.updatedAt,
  ].find(isPresent) || null;

const formatDateValue = (value) => {
  if (!isPresent(value)) return FALLBACK_LABEL;
  return formatLagosDate(
    value,
    "en-GB",
    { day: "2-digit", month: "short", year: "numeric" },
    FALLBACK_LABEL,
  );
};

const getLocation = (record, type) => {
  const location = type === "pickup" ? record.pickup : record.dropoff;
  const values = type === "pickup"
    ? [record.pickupLocationName, location?.address, location?.name, record.pickupLocation]
    : [record.dropoffLocationName, location?.address, location?.name, record.dropoffLocation];
  return values.map(asText).find(Boolean) || FALLBACK_LABEL;
};

const getDuration = (record = {}) => {
  const duration = [
    record.duration,
    record.estimatedDuration,
    record.durationMinutes,
    record.rideDuration,
    record.rideTime,
  ].find(isPresent);
  if (!isPresent(duration)) return FALLBACK_LABEL;

  const numericDuration = getNumeric(duration);
  return numericDuration === null ? String(duration) : `${numericDuration} min`;
};

export const normalizeRide = (record = {}) => {
  const status = normalizeStatus(record.status || record.rideStatus);
  const amountNumber = getMoneyInNaira(record, [
    { path: "fareAmountKobo", unit: "kobo" },
    { path: "fareKobo", unit: "kobo" },
    { path: "amountKobo", unit: "kobo" },
    "fareAmount",
    "fare",
    "amount",
  ]);
  const id = getRecordId(record) || FALLBACK_LABEL;
  const dateValue = getDateValue(record);

  return {
    id,
    dateValue,
    date: formatDateValue(dateValue),
    pickup: getLocation(record, "pickup"),
    dropoff: getLocation(record, "dropoff"),
    rideTime: getDuration(record),
    amountNumber,
    amount: formatCurrencyValue(amountNumber),
    statusType: status,
    status: formatStatusLabel(status),
    source: record,
  };
};

export const normalizeCommission = (record = {}) => {
  const grossFareNumber = getMoneyInNaira(record, [
    { path: "fareAmountKobo", unit: "kobo" },
    { path: "fareKobo", unit: "kobo" },
    "fareAmount",
    "fare",
    "grossFare",
  ]);
  const commissionNumber = getMoneyInNaira(record, [
    { path: "commissionAmountKobo", unit: "kobo" },
    { path: "commissionKobo", unit: "kobo" },
    "commissionAmount",
    "commission",
  ]);
  const payoutNumber = grossFareNumber === null
    ? null
    : Math.max(grossFareNumber - (commissionNumber || 0), 0);
  const dateValue = [record.createdAt, record.updatedAt].find(isPresent) || null;
  const rideId = asComparableId(record.rideId) || asComparableId(record.bookingId) || FALLBACK_LABEL;
  const id = getRecordId(record) || rideId;
  const status = normalizeStatus(record.status || "completed");

  return {
    id,
    dateValue,
    date: formatDateValue(dateValue),
    rideId,
    bookingType: asText(record.rideId?.bookingType || record.bookingType || record.type) || FALLBACK_LABEL,
    amountNumber: grossFareNumber,
    amount: formatCurrencyValue(grossFareNumber),
    commissionNumber,
    commission: formatCurrencyValue(commissionNumber),
    payoutNumber,
    payout: formatCurrencyValue(payoutNumber),
    method: asText(record.paymentMethod || record.method || record.paymentType) || FALLBACK_LABEL,
    statusType: status,
    status: formatStatusLabel(status),
    source: record,
  };
};

// Everything credited to a rider's wallet: ride earnings plus promotion top-ups.
// Returns naira, or null when no credit of either kind exists.
export const sumRiderCredits = (transactions = []) => {
  let rideKobo = 0;
  let promoKobo = 0;
  let found = false;

  transactions.forEach((transaction) => {
    if (String(transaction.type || "").toLowerCase() !== "credit") return;
    const source = String(transaction.source || "").toLowerCase();
    if (source !== "ride_payout" && source !== "promotion") return;
    const amount = getNumeric(transaction.amount);
    if (amount === null) return;
    found = true;
    if (source === "promotion") promoKobo += amount;
    else rideKobo += amount;
  });

  if (!found) return null;
  return { rides: rideKobo / 100, promotions: promoKobo / 100, total: (rideKobo + promoKobo) / 100 };
};

export const sumNumbers = (items = [], key) =>
  items.reduce((total, item) => {
    const value = Number(item?.[key]);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
